/**
 * appSounds.ts — Sonidos cortos y voz de la app (Web Audio + TTS nativo/Web).
 *
 * QUÉ HACE: Provee un `AudioContext` perezoso y reutilizable para tonos cortos,
 *   desbloquea audio y voz ante la política de autoplay del navegador/WebView y
 *   expone anuncios por voz: en Android usa el TTS nativo (`AltenotTts`, porque
 *   el WebView no implementa Web Speech) y en Web usa `speechSynthesis` con
 *   espera de voces, `resume` periódico y un **watchdog** que garantiza que la
 *   secuencia siempre avance aunque `onend`/`onerror` nunca se dispare. Una
 *   secuencia nueva cancela la anterior.
 * QUÉ EXPORTA: unlockAudio, unlockSpeech, preloadVoices, isSpeechSupported,
 *   ensureSpeechReady, speak, speakSequence, stopSpeaking y los beeps del POS.
 * QUÉ NO DEBE HACER: No conoce React, persistencia ni reglas de negocio.
 */
import { AltenotTts, isNativeTtsPlatform } from "@/plugins/altenotTts";

let audioContext: AudioContext | null = null;
let speechPrimed = false;
let voicesListenerAttached = false;
/** Disponibilidad del TTS nativo (null = aún sin consultar). */
let nativeTtsAvailable: boolean | null = null;
/** Promesa cacheada de disponibilidad de voz; se reintenta si falla. */
let speechReadyPromise: Promise<boolean> | null = null;

/** Tiempo máximo de espera a que el navegador cargue voces. */
const VOICES_TIMEOUT_MS = 1_500;
/** Respiro tras `cancel()`: Chrome puede descartar el `speak` inmediato. */
const INITIAL_SPEAK_DELAY_MS = 80;

/** Nivel de ganancia seguro: base × volumen (0.05–1), acotado a (0, 1]. */
const gainLevel = (base: number, volume: number): number => {
  const v = Number.isFinite(volume) ? Math.min(1, Math.max(0.05, volume)) : 1;
  return Math.min(1, Math.max(0.01, base * v));
};

/** Generación activa de voz: al cambiar, las secuencias viejas se descartan. */
let activeGeneration = 0;
let pendingTimer: number | null = null;
let keepAliveTimer: number | null = null;
/** Resolver del silencio entre segmentos (se libera al detener la voz). */
let pendingGapResolve: (() => void) | null = null;

/** Devuelve (y crea una sola vez) el AudioContext compartido. */
const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  if (!audioContext) {
    try {
      audioContext = new Ctor();
    } catch {
      audioContext = null;
    }
  }
  return audioContext;
};

/**
 * Desbloquea el audio. Debe llamarse dentro de un gesto del usuario (click,
 * tecla, toque) para que el navegador permita reproducir sonidos.
 */
export function unlockAudio(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    void ctx.resume();
  }
}

/**
 * ¿La plataforma puede reproducir voz? En Android (APK) el motor es el TTS
 * nativo; en Web/iOS, Web Speech. La comprobación fina la hace
 * `ensureSpeechReady()` (voces reales o motor disponible).
 */
export function isSpeechSupported(): boolean {
  if (isNativeTtsPlatform()) return true;
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/** Precarga las voces (algunos navegadores las cargan de forma asíncrona). */
export function preloadVoices(): void {
  if (!isSpeechSupported() || isNativeTtsPlatform()) return;
  try {
    const synth = window.speechSynthesis;
    synth.getVoices();
    if (!voicesListenerAttached) {
      voicesListenerAttached = true;
      synth.addEventListener("voiceschanged", () => synth.getVoices());
    }
  } catch {
    // Sin voz disponible.
  }
}

/**
 * Espera a que haya voces reales (Web) o a que el motor nativo esté listo
 * (Android). Se cachea el resultado positivo; si no hay voz, se puede
 * reintentar en una llamada posterior (p. ej. al instalar voces).
 */
export function ensureSpeechReady(): Promise<boolean> {
  if (speechReadyPromise) return speechReadyPromise;
  speechReadyPromise = resolveSpeechReady().then((ready) => {
    if (!ready) speechReadyPromise = null;
    return ready;
  });
  return speechReadyPromise;
}

async function resolveSpeechReady(): Promise<boolean> {
  if (isNativeTtsPlatform()) {
    // Reconsulta hasta que el motor nativo esté listo (init asíncrono).
    if (nativeTtsAvailable === true) return true;
    try {
      const result = await AltenotTts.isAvailable();
      nativeTtsAvailable = result.available;
    } catch {
      nativeTtsAvailable = false;
    }
    return nativeTtsAvailable;
  }
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    return false;
  }
  return waitForVoices(VOICES_TIMEOUT_MS);
}

/** Resuelve `true` cuando el navegador ya expone voces (o al agotar el tope). */
const waitForVoices = (timeoutMs: number): Promise<boolean> =>
  new Promise((resolve) => {
    const synth = window.speechSynthesis;
    if (synth.getVoices().length > 0) {
      resolve(true);
      return;
    }
    let done = false;
    const finish = (ready: boolean): void => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      synth.removeEventListener("voiceschanged", onVoicesChanged);
      resolve(ready);
    };
    const onVoicesChanged = (): void => finish(synth.getVoices().length > 0);
    const timer = window.setTimeout(
      () => finish(synth.getVoices().length > 0),
      timeoutMs,
    );
    synth.addEventListener("voiceschanged", onVoicesChanged);
  });

/**
 * Desbloquea la síntesis de voz dentro de un gesto del usuario. Sin esto, el
 * primer anuncio lanzado desde el polling (sin gesto) puede ignorarse.
 */
export function unlockSpeech(): void {
  if (!isSpeechSupported()) return;
  // Calienta la disponibilidad (plugin nativo o voces del navegador).
  void ensureSpeechReady();
  if (isNativeTtsPlatform()) return;
  try {
    const synth = window.speechSynthesis;
    if (!speechPrimed) {
      const silent = new SpeechSynthesisUtterance(" ");
      silent.volume = 0;
      synth.speak(silent);
      speechPrimed = true;
    }
    synth.resume();
  } catch {
    // Sin voz disponible.
  }
}

/** Beep de dos tonos para avisar que entró una orden nueva. */
export function playNewOrderBeep(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();

  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.setValueAtTime(1180, now + 0.14);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.14, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.36);
  } catch {
    // Sin audio disponible: la app sigue funcionando.
  }
}

/**
 * Beep corto de confirmación al agregar un ítem al carrito (POS/Caja).
 * Tono agudo tipo escáner de tienda: onda cuadrada y volumen alto para que
 * se oiga en un local con ruido; distinto del beep de orden nueva.
 * `volume` (0.05–1) escala el volumen según las preferencias del negocio.
 */
export function playCartAddBeep(volume = 1): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();

  try {
    const level = gainLevel(0.4, volume);
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(2200, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.004);
    gain.gain.setValueAtTime(level, now + 0.09);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.11);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.12);
  } catch {
    // Sin audio disponible: la app sigue funcionando.
  }
}

/**
 * Beep descendente de confirmación al quitar un ítem del carrito, bajar la
 * cantidad o poner la venta en espera (POS/Caja). Distinto del beep de
 * agregado para que el cajero reconozca la acción sin mirar la pantalla.
 */
export function playCartRemoveBeep(volume = 1): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();

  try {
    const level = gainLevel(0.35, volume);
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(760, now);
    osc.frequency.setValueAtTime(520, now + 0.08);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.008);
    gain.gain.setValueAtTime(level, now + 0.16);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.21);
  } catch {
    // Sin audio disponible: la app sigue funcionando.
  }
}

/**
 * Aviso de dos tonos del POS cuando llega una mesa o un pedido pendiente
 * nuevo. Más fuerte que el beep del KDS para oírse en caja.
 */
export function playPosPendingBeep(volume = 1): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();

  try {
    const level = gainLevel(0.35, volume);
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.setValueAtTime(1180, now + 0.14);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.36);
  } catch {
    // Sin audio disponible: la app sigue funcionando.
  }
}

/** Elige una voz en español si está instalada. */
const pickVoice = (
  voices: SpeechSynthesisVoice[],
): SpeechSynthesisVoice | undefined =>
  voices.find((v) => v.lang.toLowerCase().startsWith("es")) ??
  voices.find((v) => v.default);

/** Limpia los temporizadores internos de la secuencia. */
const clearPending = (): void => {
  if (pendingTimer !== null) {
    window.clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (keepAliveTimer !== null) {
    window.clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
  if (pendingGapResolve) {
    const resolve = pendingGapResolve;
    pendingGapResolve = null;
    resolve();
  }
};

/** Detiene cualquier anuncio en curso y descarta la cola pendiente. */
export function stopSpeaking(): void {
  activeGeneration += 1;
  clearPending();
  if (isNativeTtsPlatform()) {
    void AltenotTts.stop().catch(() => undefined);
  }
  try {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  } catch {
    // Sin voz disponible.
  }
}

/** Duración estimada de una frase (watchdog anti-cuelgue de la voz). */
const estimateSpeechMs = (text: string): number =>
  Math.min(40_000, Math.max(1_200, text.length * 120)) + 3_000;

/** Ejecuta una promesa con tope de tiempo (fallback si el nativo no responde). */
const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  fallback: T,
): Promise<T> =>
  new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(fallback), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      () => {
        window.clearTimeout(timer);
        resolve(fallback);
      },
    );
  });

/** Silencio entre segmentos que se libera al detener la voz. */
const waitBetweenSegments = (
  ms: number,
  myGeneration: number,
): Promise<boolean> =>
  new Promise((resolve) => {
    pendingGapResolve = () => resolve(myGeneration === activeGeneration);
    pendingTimer = window.setTimeout(() => {
      pendingTimer = null;
      pendingGapResolve = null;
      resolve(myGeneration === activeGeneration);
    }, ms);
  });

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const makeUtterance = (text: string, volume = 1): SpeechSynthesisUtterance => {
  const synth = window.speechSynthesis;
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = "es-CO";
  utter.rate = 1;
  utter.pitch = 1;
  utter.volume = Math.max(0, Math.min(1, volume));
  const voice = pickVoice(synth.getVoices());
  if (voice) utter.voice = voice;
  return utter;
};

/** Anuncia una frase con el motor nativo (Android) con tope de espera. */
const speakNativeSegment = async (
  text: string,
  volume: number,
): Promise<void> => {
  try {
    await withTimeout(
      AltenotTts.speak({ text, volume, flush: true }),
      estimateSpeechMs(text),
      { spoken: false },
    );
  } catch {
    // Best-effort: la secuencia continúa con el siguiente segmento.
  }
};

/** Mantiene despierto el motor de voz (Chrome lo pausa a veces). */
const startKeepAlive = (synth: SpeechSynthesis, myGeneration: number): void => {
  if (keepAliveTimer !== null) return;
  keepAliveTimer = window.setInterval(() => {
    if (myGeneration !== activeGeneration) {
      clearPending();
      return;
    }
    if (synth.speaking) synth.resume();
  }, 10_000);
};

/** Cola de frases por Web Speech con watchdog por frase. */
const runWebSequence = (
  texts: string[],
  gap: number,
  volume: number,
  myGeneration: number,
  onDone?: () => void,
): void => {
  const synth = window.speechSynthesis;
  const queue = [...texts];

  const playNext = (): void => {
    if (myGeneration !== activeGeneration) return;
    const text = queue.shift();
    if (text === undefined) {
      clearPending();
      onDone?.();
      return;
    }

    let settled = false;
    let watchdog: number | null = null;
    const advance = (): void => {
      if (settled) return;
      settled = true;
      if (watchdog !== null) {
        window.clearTimeout(watchdog);
        watchdog = null;
      }
      if (myGeneration !== activeGeneration) return;
      if (queue.length === 0) {
        clearPending();
        onDone?.();
        return;
      }
      pendingTimer = window.setTimeout(playNext, gap);
    };

    const utter = makeUtterance(text, volume);
    utter.onend = advance;
    utter.onerror = advance;
    startKeepAlive(synth, myGeneration);
    synth.resume();
    synth.speak(utter);
    // Watchdog: si la plataforma nunca dispara onend/onerror, avanza igual.
    watchdog = window.setTimeout(advance, estimateSpeechMs(text));
  };

  playNext();
};

/** Ejecuta la secuencia en el motor que corresponda a la plataforma. */
const runSequence = async (
  texts: string[],
  gap: number,
  volume: number,
  myGeneration: number,
  onDone?: () => void,
): Promise<void> => {
  const ready = await ensureSpeechReady();
  if (myGeneration !== activeGeneration) return;
  if (!ready) {
    onDone?.();
    return;
  }

  if (isNativeTtsPlatform()) {
    for (let index = 0; index < texts.length; index += 1) {
      if (myGeneration !== activeGeneration) return;
      await speakNativeSegment(texts[index], volume);
      if (index < texts.length - 1) {
        const keepGoing = await waitBetweenSegments(gap, myGeneration);
        if (!keepGoing) return;
      }
    }
    if (myGeneration === activeGeneration) onDone?.();
    return;
  }

  // Respiro tras `cancel()`: Chrome puede descartar el `speak` inmediato.
  await delay(INITIAL_SPEAK_DELAY_MS);
  if (myGeneration !== activeGeneration) return;
  runWebSequence(texts, gap, volume, myGeneration, onDone);
};

/**
 * Reproduce una secuencia de textos, uno tras otro, con una pausa `gapMs` entre
 * cada uno. Llama a `onDone` solo si la secuencia termina de forma natural (no
 * si fue cancelada por otra secuencia o por `stopSpeaking`). Si no hay voz
 * funcional, llama a `onDone` de inmediato (permite seguir con el fallback).
 */
export function speakSequence(
  texts: string[],
  options?: { gapMs?: number; volume?: number; onDone?: () => void },
): void {
  if (texts.length === 0) {
    options?.onDone?.();
    return;
  }

  stopSpeaking();
  const myGeneration = activeGeneration;
  const gap = options?.gapMs ?? 2_500;
  const volume = options?.volume ?? 1;
  void runSequence(texts, gap, volume, myGeneration, options?.onDone);
}

/** Anuncia un único texto por voz (cancela lo que esté sonando). */
export function speak(text: string, volume = 1): void {
  speakSequence([text], { gapMs: 0, volume });
}
